import React from "react";

declare const appInsights: any;
import JSONbig from "json-bigint";
import _ from "lodash";
import moment from "moment";
import urlJoin from "url-join";
import humanizeString from 'humanize-string';

import Toastitfy from "./components/Toastify"

const permissionKey = "frends_permissions";
import { OptionTypeBase, Styles } from "react-select";
import { Action, Dispatch } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { LoopParameter } from "../processDetails/Models";
import { HashMap } from "./model/HashMap";
import { Option, FixedOption } from "./model/Option";

export type ThunkResult<R, S> = ThunkAction<R, S, null, Action>;
export type DispatchType<S> = Dispatch<Action> & ThunkDispatch<S, any, Action>;

export class Utilities {

    public static selectStyles: Partial<Styles<OptionTypeBase, false>> = {
        control: (provided, state) => {
            let maxHeight = "34px";
            if (state.isMulti) {
                const values = state.getValue();
                const valueLen = values.map((x) => x.label).join("").length;
                if (valueLen > 40 || values.length > 3) {
                    maxHeight = "0px";
                }
            }
            return ({
                ...provided,
                borderRadius: "0px",
                maxHeight: maxHeight,
                minHeight: "0px"
            });
        },
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            borderRadius: "0px",
        }),
        valueContainer: (provided, state) => {
            let padding = "2px 8px 6px";
            if (state.isMulti) {
                const values = state.getValue();
                const valueLen = values.map((x) => x.label).join("").length;
                if (valueLen > 40 || values.length > 3) {
                    padding = "2px 8px";
                }
            }
            return ({
                ...provided,
                padding: padding
            });
        },

        multiValue: (base, state) => {
            return state.data.isFixed ? {
                ...base,
                backgroundColor: "gray"
            } : {
                ...base,
                backgroundColor: "#FF0096"
            };
        },
        multiValueLabel: (base, state) => {
            return state.data.isFixed
                ? {
                    ...base,
                    fontWeight: 'bold',
                    color: 'white',
                    paddingRight: 6,
                }
                : {
                    ...base,
                    color: "white",
                    padding: "2px",
                    whiteSpace: "initial"
                };
        },
        multiValueRemove: (base, state) => {
            return state.data.isFixed ? {
                ...base,
                display: 'none'
            } : {
                ...base,
                color: "white",
                "&:hover": {
                    backgroundColor: "#7C3A8E"
                }
            };
        }
    };

    public static switchStyles = {
        onColor: "#337ab7",
        offColor: "#ccc"
    };

    public static getAllUserPermissions(): HashMap<boolean> {
        function getCookies() {
            const cookies: { [key: string]: string } = {};
            for (const cookie of document.cookie.split("; ")) {
                const [name, value] = cookie.split("=");
                cookies[name] = decodeURIComponent(value);
            }
            return cookies;
        }
        const permissionMap = getCookies()[permissionKey];

        return permissionMap ? JSON.parse(permissionMap) : {};
    }

    public static getUserPermission(activity: string): boolean {
        const allPermissions = this.getAllUserPermissions();
        return allPermissions[activity];
    }

    public static humanizeLabel(str: string | undefined): string | undefined {
        if (str) {
            return capitalizeLastWordIfId(humanizeString(str));
        }
        return str;
    }

    public static valueFromId = (opts: Option[] | null | undefined, id: string | number | undefined | null) => opts?.find((o) => o.value === id);
    public static valuesFromList = (opts: Option[] | null | undefined, ids: string[] | number[]) => opts?.filter((o) => ids.some((id: string | number) => id === o.value));

    public static valueFromListWithFixed = (opts: FixedOption[], ids: string[] | number[] | undefined) => opts.filter((o) => ids?.some((id: any) => id === o.value));

    public static postJsonAndParseResponse<T>(url: string, content: any): Promise<T> {
        return Utilities.fetchBase(url, content, "POST").then<T>(this.parseJsonResponse);
    }

    public static putJsonAndParseResponse<T>(url: string, content: any): Promise<T> {
        return Utilities.fetchBase(url, content, "PUT").then<T>(this.parseJsonResponse);
    }

    public static patchJsonAndParseResponse<T>(url: string, content: any): Promise<T> {
        return Utilities.fetchBase(url, content, "PATCH").then<T>(this.parseJsonResponse);
    }

    public static getQuery(url: string, queryStringParameters?: { [key: string]: string | string[] }): string {
        let fetchUrl = url;
        if (queryStringParameters) {
            // original from https://stackoverflow.com/a/34209399
            const query = Object.keys(queryStringParameters)
                .filter((k) => queryStringParameters[k] != null)
                .map((k) => {
                    const key = encodeURIComponent(k);
                    const val = queryStringParameters[k];
                    if (Array.isArray(val)) {
                        return key + "=" + val.map((v) => encodeURIComponent(v)).join(`&${key}=`);
                    } else {
                        return key + "=" + encodeURIComponent(val);
                    }
                })
                .join("&");
            if (query) {
                fetchUrl = url + "?" + query;
            }
        }
        return fetchUrl;
    }

    public static getFetch<T>(url: string, queryStringParameters?: { [key: string]: string | string[] }): Promise<T> {
        const fetchUrl = this.getQuery(url, queryStringParameters);
        return fetch(fetchUrl, {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" }
        }).then<T>(Utilities.parseJsonResponse);
    }

    public static async getFetchWithHeaders<T>(url: string, queryStringParameters?: { [key: string]: string | string[] }): Promise<{ content: T, headers: Headers }> {
        try {
            const fetchUrl = this.getQuery(url, queryStringParameters);
            const fetchResponse = await fetch(fetchUrl, {
                credentials: "same-origin",
                headers: { "X-Requested-With": "XMLHttpRequest" }
            });
            const content = await Utilities.parseJsonResponse<T>(fetchResponse);
            return {
                content,
                headers: fetchResponse.headers
            }
        }
        catch (e) {
            console.error(e);
            throw(e);
        }
    }

    public static deleteFetchAndParseResponse<T>(url: string): Promise<T> {
        return fetch(url, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "X-XSRF-Token": Utilities.getXSRFVerificationToken(),
                "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "same-origin"
        }).then<T>(this.parseJsonResponse);
    }

    public static parseJsonResponse<T>(response: Response, customErrorParser?: (msg: any) => string | undefined): Promise<T> {
        return response.text()
            .then((content) => {
                if (!response.ok) {
                    let errorContent;
                    try {
                        const errorMsg = JSON.parse(content);
                        if (customErrorParser) {
                            errorContent = customErrorParser(errorMsg);
                        }
                        if (!errorContent) {
                            if (errorMsg["modelState"]) {
                                errorContent = [].concat.apply([],
                                    Object.keys(errorMsg["modelState"]).map((key) => errorMsg["modelState"][key])
                                ).join("<br/>");
                            } else {
                                errorContent = errorMsg["exceptionMessage"] || errorMsg["message"] || errorMsg;
                            }
                        }
                    } catch (e) {
                        errorContent = content; // ignore, just return as is
                    }

                    return Promise.reject(`Error (${response.status}): ${errorContent}`);
                } else {
                    // use text just in case the returned value is not valid json (e.g. empty)
                    return Promise.resolve(Utilities.getPossibleJsonFromText(content));
                }
            });
    }

    public static getErrorTitle(err: Error): string {
        return _.has(err, "message") ? err.message : "Error";
    }

    public static getErrorText(err: Error & { exceptionMessage?: string, }): string {
        // If error is from backend it usually has an exceptionMessage
        if (_.has(err, "exceptionMessage")) {
            return err.exceptionMessage ?? "Error";
        }

        // If error is from javascript it probably has stack;
        if (_.has(err, "stack")) {
            return err.stack ?? "Error";
        }

        // Test if it has message
        if (_.has(err, "message")) {
            return err.message;
        }

        // If error is a simple string we can return it        
        if (_.isString(err)) {
            return err;
        }

        return "Error";
    }

    public static getStepIdFromParameterKey(loopParameters: LoopParameter[]): string | null {
        if (!loopParameters) {
            return null;
        }
        return loopParameters.map((parameter) => parameter.key + parameter.value).join();
    }

    public static getLoopParameterDictionary(loopParameters: LoopParameter[]): { [loopId: string]: number } {
        const ret: { [key: string]: number } = {};
        if (!loopParameters) {
            return ret;
        }
        for (const l of loopParameters) {
            ret[l.key] = l.value;
        }
        return ret;
    }

    public static toDetailedLocalTime(d: string | Date | undefined) {
        if (d) {
            return moment.utc(d).local().format("YYYY-MM-DD HH:mm:ss");
        } else {
            return FRENDS.Localizations.Never;
        }
    }

    public static getXSRFVerificationToken(): string {
        return document.getElementsByName("__RequestVerificationToken")[0]?.getAttribute("value") ?? "";
    }

    public static getBaseUrlPath = (appRootUrl: string): string => {
        const currentPath = document.location.pathname || appRootUrl;
        const startOfAppBase = currentPath.indexOf(appRootUrl);

        let base: string = currentPath;

        if (startOfAppBase !== -1) {
            base = currentPath.substr(0, startOfAppBase);
        }

        base = urlJoin(base, appRootUrl);

        return base;
    };


    /**
     * Navigates the browser to the given path, relative to the declared base path
     * This is needed because IE does not like the base paths...
     * @param path relative path, no leading slash needed
     */
    public static navigateTo(path: string) {
        const base = document.getElementsByTagName("base")[0].href;
        const newProcessUrl = base + path;
        setTimeout(() => window.location.href = newProcessUrl, 1000);
    }

    public static toastSuccess(message: string, keepOpen: boolean = false, showCloseButton: boolean = true) {
        Toastitfy(message, { type: "success", closeButton: showCloseButton, closeOnClick: true, autoClose: keepOpen ? false : 5000 });
    }

    public static toastWarning(message: string, keepOpen: boolean = false, showCloseButton: boolean = true) {
        Toastitfy(message, { type: "warning", closeButton: showCloseButton, closeOnClick: true, autoClose: keepOpen ? false : 8000 });
    }

    public static toastError(message: string, keepOpen: boolean = true, showCloseButton: boolean = true) {
        // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md
        if (typeof appInsights !== "undefined") {
            appInsights.trackException(message, "Toast", { ["Toast"]: "error" }, null, 2); // 2 = warning;
        }

        Toastitfy(
            <div>{message}
                <button
                    className="Toastify__close-button Toastify__close-button--light"
                    style={{ marginLeft: "5px" }}
                    onClick={() => navigator.clipboard.writeText(message)}>
                    <i className="fa fa-copy" />
                </button>
            </div>, {
            type: "error", closeButton: showCloseButton,
            closeOnClick: true, autoClose: keepOpen ? false : 8000
        });
    }

    public static fetchBase(url: string, content: any, method: string) {
        return fetch(url, {
            method: method,
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "X-XSRF-Token": Utilities.getXSRFVerificationToken(),
                "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "same-origin",
            body: content && JSON.stringify(content)
        });
    }

    public static arrayToObject<T, K extends keyof T>(array: T[], keyField: K, itemField?: K | null) {
        return array.reduce((obj, item) => {
            obj[item[keyField]] = itemField != null ? item[itemField] : item;
            return obj;
        }, {} as any);
    }

    private static getPossibleJsonFromText(text: string) {
        if (text.length > 0) {
            try {
                return JSONbig.parse(text);
            } catch (e) {
                // tslint:disable-next-line:no-console
                console.debug("Response not valid json: " + text, e);
            }
        }
        return text; // just return whatever
    }
}

function capitalizeLastWordIfId(s: string) {
    const n = s.split(" ");
    if (n[n.length - 1] === "id") {
        n[n.length - 1] = n[n.length - 1].toUpperCase();
    }
    return n.join(" ");
}

export function isEmptyObject(obj: object) {
    return Object.getOwnPropertyNames(obj).length === 0;
}

export function printWarning(msg: string) {
    if (typeof console !== "undefined") {
        // tslint:disable-next-line:no-console
        console.warn(msg);
    }
}

export function printError(msg: string) {
    if (typeof console !== "undefined") {
        // tslint:disable-next-line:no-console
        console.error(msg);
    }
}
// Taken from https://basarat.gitbooks.io/typescript/docs/types/literal-types.html
export function strEnum<T extends string>(o: T[]): { [K in T]: K } {
    return o.reduce((res, key) => {
        res[key] = key;
        return res;
    }, Object.create(null));
}

export function openInNewTabClick<T>(event: React.MouseEvent<T>): boolean {
    // button = 0 is left click, ctrlKey + left click opens in new tab, shift + left click opens in new window
    if (event.button === 0 && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        return false;
    }
    return true;
}
